import React, { useEffect, useMemo, useRef } from "react";

import { useProductColors } from "../../../hooks/useProductColors";

import { ColorBottomMenuPanel, IColorBottomMenuPanelRef } from "../../bottom-menu-panels/color";
import { useBackground } from "./hooks/useBackground";
import { useSetColorBackground } from "./hooks/useSetColorBackground";
import { useUsedBackgrounds } from "./hooks/useUsedBackgrounds";

export const BackgroundColorPanelContainer = () => {
	// use memo?
	const panelRef = useRef<IColorBottomMenuPanelRef>(null);
	const { usedColors } = useUsedBackgrounds();
	const background = useBackground();
	const setColorBackground = useSetColorBackground();

	// Get Colors lists
	const { data: productColors = [] } = useProductColors();

	// The colors to show is a combination of different lists
	// Colors missing in the list provided by the server happens quite a lot
	const colorsToShow = useMemo<Array<eXpress.core.IColor>>(
		() =>
			usedColors
				.filter(
					(color) =>
						!productColors?.some((prdColor) => prdColor.hexColorString === color),
				) // only add colors at front that are not in the sorted palette (else distinct will move color)
				.sort() // to prevent that order changes if a zone is removed.
				.map((color) => ({
					// Map usedColors to IColor structure
					hexColorString: color,
					isFluo:
						!!productColors?.find(
							(productColor) => productColor.hexColorString === color,
						)?.isFluo || false,
				}))
				.concat(productColors), // Don't sort palette from server
		[productColors, usedColors],
	);

	// Scroll into view if background is changed (animated)
	useEffect(() => {
		// scroll selection into view if a new background is selected
		if (panelRef.current && background instanceof eXpress.core.ColorBackground) {
			if (panelRef.current) panelRef.current.scrollIntoView(background.color.webColor, true);
		}
	}, [background]);

	// Scroll into view if background color list is loaded (not animated)
	useEffect(() => {
		if (panelRef.current && background instanceof eXpress.core.ColorBackground) {
			panelRef.current.scrollIntoView(background.color.webColor, false); // This will override the animation
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productColors]);

	if (!setColorBackground) return null;

	return (
		<ColorBottomMenuPanel
			colors={colorsToShow}
			selectedColor={
				background instanceof eXpress.core.ColorBackground
					? eXpress.HexColorString.fromString(background?.color.webColor)
					: undefined
			}
			onClick={(color) => {
				setColorBackground(color.hexColorString);
			}}
			ref={panelRef}
		/>
	);
};
BackgroundColorPanelContainer.displayName = "BackgroundColorPanelContainer";
